import React, { useMemo, useState, type DragEvent } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dico from "../../../util/src/language/dico.js";
import { getValueTypeColor } from "../../../util/src/blueprint/method.js";
import type { SceneBehaviorCatalog } from "../display-editor/layout-types";
import type { BehaviorDraggedNodeTemplate } from "../../../util/src/blueprint/types.js";
import { BEHAVIOR_NODE_DRAG_MIME } from "./BehaviorNodeList.js";
import {
  getSceneCatalogReferenceCatalog,
  type SceneCatalogReferenceItem,
  type SceneCatalogReferenceSection,
} from "./SceneCatalogTemplateCatalog";
import { matchesPropertySearch } from "../display-editor/editor/property-panel/propertySearch";

export interface SceneCatalogNodeListProps {
  catalog: SceneBehaviorCatalog;
  readOnly?: boolean;
  /** When set, only these catalog sections appear in the right panel. */
  visibleSections?: SceneCatalogReferenceSection[];
  /** Custom event ID to omit from the events section (self-reference guard). */
  excludeCustomEventId?: string;
  editingObjectTypeId?: string;
}

const NODE_BUTTON_SX = {
  display: "block",
  width: "100%",
  border: "1px solid transparent",
  borderRadius: 1,
  py: 0.75,
  px: 1,
  backgroundColor: "transparent",
  boxSizing: "border-box",
  textAlign: "left",
  fontSize: 13,
  lineHeight: 1.4,
  color: "text.primary",
  cursor: "grab",
  "&:active": { cursor: "grabbing" },
  "&:hover": { backgroundColor: "action.hover" },
} as const;

const TYPE_DOT_STYLE = {
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: 99,
  marginRight: 8,
} as const;

type SectionKey = SceneCatalogReferenceSection;

interface SectionData {
  key: SectionKey;
  label: string;
  items: SceneCatalogReferenceItem[];
}

const SceneCatalogNodeListComponent: React.FC<SceneCatalogNodeListProps> = ({
  catalog,
  readOnly = false,
  visibleSections,
  excludeCustomEventId,
  editingObjectTypeId,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const d = (dico().behavior as Record<string, unknown>).sceneCatalog as
    | Record<string, unknown>
    | undefined;
  const panelTitle =
    (d as Record<string, string> | undefined)?.panelTitle ?? "Scene catalog";
  const searchPlaceholder =
    (d as Record<string, string> | undefined)?.searchPlaceholder ?? "";

  const referenceItems = useMemo(
    () =>
      getSceneCatalogReferenceCatalog(catalog, {
        excludeCustomEventId,
        editingObjectTypeId,
        valueOnly: !!editingObjectTypeId,
      }),
    [catalog, excludeCustomEventId, editingObjectTypeId],
  );

  const sections = useMemo<SectionData[]>(() => {
    const labels: Record<SectionKey, string> = {
      objects:
        (d as Record<string, string> | undefined)?.panelObjects ?? "Objects",
      animations:
        (d as Record<string, string> | undefined)?.panelAnimations ??
        "Animations",
      events:
        (d as Record<string, string> | undefined)?.panelEvents ?? "Events",
      functions:
        (d as Record<string, string> | undefined)?.panelFunctions ??
        "Functions",
      variables:
        (d as Record<string, string> | undefined)?.panelVariables ??
        "Variables",
    };
    const order: SectionKey[] = visibleSections ?? [
      "animations",
      "events",
      "functions",
      "variables",
      "objects",
    ];
    return order.map((key) => {
      const sectionLabel = labels[key];
      const sectionMatches = matchesPropertySearch(searchQuery, sectionLabel);
      const items = referenceItems.filter((item) => {
        if (item.section !== key) return false;
        if (sectionMatches) return true;
        return matchesPropertySearch(searchQuery, item.label, sectionLabel);
      });
      return {
        key,
        label: sectionLabel,
        items,
      };
    });
  }, [d, referenceItems, searchQuery, visibleSections]);

  const emptyLabel =
    (d as Record<string, string> | undefined)?.panelEmpty ?? "";

  const startDrag = (
    event: DragEvent<HTMLButtonElement>,
    template: BehaviorDraggedNodeTemplate,
  ) => {
    const payload = JSON.stringify(template);
    event.dataTransfer.setData(BEHAVIOR_NODE_DRAG_MIME, payload);
    event.dataTransfer.setData("text/plain", payload);
    event.dataTransfer.effectAllowed = "copy";
  };

  return (
    <Box
      data-behavior-palette
      sx={{
        width: 220,
        minWidth: 220,
        maxWidth: 220,
        position: "relative",
        zIndex: 2,
        borderLeft: "1px solid #e0e0e0",
        backgroundColor: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        height: "100%",
        boxSizing: "border-box",
        alignSelf: "stretch",
        ...(readOnly ? { pointerEvents: "none", opacity: 0.5 } : {}),
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1,
          pt: 1,
          pb: 1,
          backgroundColor: "#fff",
          borderBottom: "1px solid #eee",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ px: 1, pb: 1, fontWeight: 600, color: "text.secondary" }}
        >
          {panelTitle}
        </Typography>
        <Box sx={{ px: 1, width: "100%", boxSizing: "border-box" }}>
          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={searchPlaceholder}
          />
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          p: 1,
        }}
      >
        {sections
          .filter((section) => !searchQuery.trim() || section.items.length > 0)
          .map((section) => (
            <Accordion
              key={section.key}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 1, minHeight: "32px !important" }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {section.label}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pt: 0.25 }}>
                {section.items.length === 0 ? (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ px: 2, pb: 1, display: "block" }}
                  >
                    {emptyLabel}
                  </Typography>
                ) : (
                  <Stack spacing={0.6}>
                    {section.items.map((item) => (
                      <Box
                        key={item.id}
                        component="button"
                        type="button"
                        draggable
                        onDragStart={(event: DragEvent<HTMLButtonElement>) =>
                          startDrag(event, item.template)
                        }
                        sx={NODE_BUTTON_SX}
                      >
                        <span
                          style={{
                            ...TYPE_DOT_STYLE,
                            backgroundColor: getValueTypeColor(item.dotType),
                          }}
                        />
                        {item.label}
                      </Box>
                    ))}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
      </Box>
    </Box>
  );
};

export const SceneCatalogNodeList = React.memo(SceneCatalogNodeListComponent);
